import { AnyProvider, MapAnyProvider } from './types';

/**
 * Injector Class represents the dependency injection coding pattern, in which a class receives its dependencies
 * from external sources rather than creating them itself. The injector class will be used for those cases where
 * modules will need to receive external instances from outside.
 */
export class Injector {

  /** Map of provider instances */
  private providers: MapAnyProvider = {};

  /**
   * Sets a new provider instance inside the injector map.
   * @param {string} token - provider id
   * @param {AnyProvider} provider - provider instance
   * @returns {Injector}
   * @throws
   */
  public set(token: string, provider: AnyProvider): Injector | never {
    if (this.providerExists(token)) {
      throw new Error(`The provider with "Token ${token}" already exists`);
    }

    this.providers[token] = provider;

    return this;
  }

  /**
   * Sets a new provider instance inside the injector map even if there is already one with the same token.
   * @param {string} token - provider id
   * @param {AnyProvider} provider - provider instance
   * @returns {Injector}
   * @throws
   */
  public forceSet(token: string, provider: AnyProvider): Injector {
    this.providers[token] = provider;

    return this;
  }

  /**
   * Gets the instance of the provider.
   * @param {string} token - provider id
   * @return {AnyProvider} provider instance
   * @throws
   */
  public get<T = AnyProvider>(token: string): T | never {
    if (!this.providerExists(token)) {
      throw new Error(`The provider with "Token ${token}" does not exists`);
    }

    return this.providers[token] as T;
  }

  /**
   * Checks if the provider already exists as an instance into the map of providers.
   * @param {string} token - provider id
   * @returns {boolean}
   */
  public providerExists(token: string): boolean {
    return Object.keys(this.providers).includes(token);
  }
}
